import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnack } from "hooks/useSnack";

import Composer from "./components/Composer";
import MessageThread from "./components/MessageThread";
import { useMessageActions, useMessages, useMySide } from "../../messagesApi";
import { ConversationView } from "../../types";

interface ConversationThreadProps {
  conversation: ConversationView;
}

// The live body of a conversation — message feed plus composer, with
// polling, read-marking and sending wired up. Shared by the full-page
// ChatWindow and the floating ChatPopups.
const ConversationThread: React.FC<ConversationThreadProps> = ({
  conversation,
}) => {
  const showSnack = useSnack();
  const { t } = useTranslation("messages");
  const mySide = useMySide();

  const { messages, loading, hasMore, loadOlder, refresh } = useMessages(
    conversation.id,
  );
  const { send, markRead } = useMessageActions();
  const [sending, setSending] = useState(false);

  // Clear the unread badge on open, and again when polling delivers new
  // messages while the thread is actually in front of the user.
  const lastMessageId = messages.length
    ? messages[messages.length - 1].id
    : null;
  useEffect(() => {
    if (conversation.unreadCount > 0 && document.hasFocus()) {
      markRead(conversation.id);
    }
  }, [conversation.id, conversation.unreadCount, lastMessageId, markRead]);

  const handleSend = async (body: string, files: File[]): Promise<boolean> => {
    setSending(true);
    const result = await send(conversation.id, body, files);
    setSending(false);
    if (!result.ok) {
      showSnack(result.error ?? t("snack.send_failed"), "error");
      return false;
    }
    // Pull the new message in right away instead of waiting for the poll.
    refresh();
    return true;
  };

  return (
    <>
      <MessageThread
        messages={messages}
        mySide={mySide}
        hasMore={hasMore}
        loading={loading}
        onLoadOlder={loadOlder}
      />

      <Composer sending={sending} onSend={handleSend} />
    </>
  );
};

export default ConversationThread;
